import type {
  CodeableConcept,
  DocumentReference,
  Patient,
  Reference,
} from '@medplum/fhirtypes'
import { DOCUMENT_REVIEW_TASK_TITLE_FAILED_SYSTEM } from '#src/codeSystems.ts'
import type {
  DocumentCategory,
  DocumentSource,
} from '#src/models/document-references/constants.ts'
import type { ExtendedDocumentReference } from '#src/models/document-references/documentReferences.ts'
import { TASK_TYPES } from '#src/models/tasks/constants.ts'
import {
  StandardTask,
  type StandardTaskParams,
} from '#src/models/tasks/standard/standardTask.ts'
import { Role } from '#src/roles.ts'
import type { QueryTypes } from '#src/search.ts'

export type SplitBlockReason =
  | 'document-deleted'
  | 'task-completed'
  | 'task-canceled'
  | 'no-pdf-attachment'

export interface FaxInboxQueryOptions {
  state?: StandardTask['state'][]
  priority?: (StandardTask['priority'] | 'none')[]
  category?: DocumentCategory[]
  source?: DocumentSource[]
}

export interface DocumentReviewTaskParams extends StandardTaskParams {
  document: Reference<DocumentReference>
}

export class DocumentReviewTask extends StandardTask {
  static defaultRoles = [Role.MA, Role.LVN]
  static defaultTitle = 'Document Review'

  static byDocument(document: Reference<DocumentReference>): QueryTypes {
    return {
      focus: document.reference,
    }
  }

  /*
   * Returns query params for active (non-completed) review tasks for a document.
   * Used to determine whether an "Assign for Review" button should be shown —
   * completed tasks should not suppress the button.
   */
  static byDocumentActive(document: Reference<DocumentReference>): QueryTypes {
    return {
      focus: document.reference,
      code: DocumentReviewTask.code,
      'business-status': ['todo', 'in-progress', 'backlog'],
      'status:not': ['entered-in-error', 'completed', 'cancelled'],
    }
  }

  // Bulk form of `byDocumentActive`, for reading the open review work of many
  // documents with one search.
  static byDocumentsActive(
    documents: Reference<DocumentReference>[]
  ): QueryTypes {
    return {
      focus: documents.map((document) => document.reference),
      code: DocumentReviewTask.code,
      'business-status': ['todo', 'in-progress', 'backlog'],
      'status:not': ['entered-in-error', 'completed', 'cancelled'],
      _count: 1000,
    }
  }

  static faxInboxQuery(options?: FaxInboxQueryOptions): QueryTypes {
    const query: QueryTypes = {
      _sort: '-authored-on',
      _count: 1000,
      code: DocumentReviewTask.code,
      'status:not': 'entered-in-error',
      'focus:DocumentReference.status:not': 'entered-in-error',
    }

    if (options?.state?.length) {
      query['business-status'] = options.state
    }

    if (options?.priority?.length) {
      query.priority = options.priority
    }

    if (options?.category?.length) {
      query['focus:DocumentReference.category'] = options.category
    }

    if (options?.source?.length) {
      query['focus:DocumentReference._tag'] = options.source
    }

    return query
  }

  static byParticipantPending(participant: Reference<Patient>): QueryTypes {
    return {
      ...this.faxInboxQuery({ state: ['todo', 'in-progress', 'backlog'] }),
      'focus:DocumentReference.subject': participant,
    }
  }

  static getSplitBlockReason(
    docRef: ExtendedDocumentReference,
    task: DocumentReviewTask
  ): SplitBlockReason | null {
    if (docRef.status === 'entered-in-error') {
      return 'document-deleted'
    }
    if (task.isDone) {
      return 'task-completed'
    }
    if (task.state === 'canceled') {
      return 'task-canceled'
    }
    if (!docRef.attachments.some((a) => a.contentType === 'application/pdf')) {
      return 'no-pdf-attachment'
    }
    return null
  }

  constructor({ document, ...rest }: DocumentReviewTaskParams) {
    super({
      ...rest,
      code: DocumentReviewTask.code,
      focus: document,
    })
    this.title = DocumentReviewTask.defaultTitle
    this.description = 'Review and file the document'
    this.state = 'todo'
  }

  static get code(): CodeableConcept {
    return TASK_TYPES.DOCUMENT_REVIEW_TASK
  }

  get document(): Reference<DocumentReference> {
    return this.focus as Reference<DocumentReference>
  }

  get titleGenerationFailed(): boolean {
    return !!this.getTag(DOCUMENT_REVIEW_TASK_TITLE_FAILED_SYSTEM)
  }

  set titleGenerationFailed(failed: boolean) {
    this.setTagsBySystem(
      DOCUMENT_REVIEW_TASK_TITLE_FAILED_SYSTEM,
      failed ? [{ code: 'failed' }] : []
    )
  }

  // True when this review task has been explicitly assigned to a reviewer but
  // is still unstarted ("todo") — i.e. it should not be sitting in the Document
  // Inbox's "Not Started" queue. An explicit assignment is one where a
  // requester (the assigner) is recorded and differs from the owner: that is
  // what distinguishes a task someone routed for review from an auto-created
  // triage task (no requester) or a self-owned upload (requester === owner).
  get isUnstartedAssignedReview(): boolean {
    const ownerRef = this.ownerPractitioner?.reference
    const requesterRef = this.requesterPractitioner?.reference
    return (
      this.state === 'todo' &&
      !!ownerRef &&
      !!requesterRef &&
      requesterRef !== ownerRef
    )
  }
}
